import {wrapAppWithDataFetcherProvider} from '@aboutyou/data-fetcher';
import {AB_TEST_COOKIES, getAbTestGroups} from '@aboutyou/feature-flags';
import {HeadContext} from '@aboutyou/head-elements';
import {capturePreHydrationSnapshot} from '@aboutyou/lazy-imports';
import {Log} from '@aboutyou/mlm';
import {getCookieDomainFromHostname, parseCookies} from '@aboutyou/storage';
import {UIProvider} from '@aboutyou/ui-components';
import type {Request} from 'express';
import React from 'react';
import {createRoot, hydrateRoot} from 'react-dom/client';
import {IconsCache} from 'react-lazy-svg';
import {ensurePolyfillsLoaded} from './polyfills/ensurePolyfillsLoaded';
import {BaseProvider} from './providers/BaseProvider';
import {loadClientIconSVGLoader} from './providers/loadClientIconSVGLoader';
import {
  registerServiceWorker,
  unregisterAllServiceWorker,
} from './serviceWorker/registerServiceWorker';
// eslint-disable-next-line no-restricted-imports
import {getStaticConfig} from './staticConfig/getStaticConfig';
import {initializeStaticConfig} from './staticConfig/initializeStaticConfig';
import './utils/collectUnhandledErrors';
import {getBrowserAuthToken} from './utils/getBrowserAuthToken';
import {
  getServerEnableBotModeConfigOnClient,
  initializeBotModeConfig,
} from './utils/getServerBotModeOnClient';
import {
  getServerCookiesOnClient,
  initializeServerCookies,
} from './utils/getServerCookiesOnClient';
import {reportWebVitals} from './utils/performance-logging/reportWebVitals';

interface StartConfig {
  components: {
    LegacyRootProvider?: React.ComponentType<
      React.PropsWithChildren<{serverRequest?: Request}>
    >;
    App: React.ComponentType<React.PropsWithChildren>;
  };
  serviceWorker?: boolean;
}

const startReactClientHydration = async (config: StartConfig) => {
  try {
    await ensurePolyfillsLoaded();
  } catch (error) {
    // log the failure but try to continue, maybe the required polyfills are not required on this page anyhow (this will most probably fail)
    Log.error('@aboutyou/bootstrap', 'Failed to load required polyfills.', {
      error,
    });
  }

  const {LegacyRootProvider = React.Fragment, App} = config.components;
  const container = document.querySelector('main')!;

  await Promise.all([
    initializeStaticConfig(),
    initializeServerCookies(),
    initializeBotModeConfig(),
  ]);

  const staticConfig = getStaticConfig();
  const {hostConfig, grpcConfig, i18nConfig, routesConfig, enableRealUserMode} =
    staticConfig;

  const defaultCookieDomain = getCookieDomainFromHostname(
    staticConfig.hostConfig.hostname,
  );

  Log.setCustomAttribute(
    'enableRealUserMode',
    enableRealUserMode ? 'true' : 'false',
  );

  const headContext: HeadContext = {};

  const iconsCache: IconsCache = new Map();
  UIProvider.initClient(iconsCache);

  const loadIconSVG = loadClientIconSVGLoader({
    iconsCache,
    assetsUrl: hostConfig.assetsUrl,
  });

  const serverCookies = getServerCookiesOnClient();
  const browserCookies = parseCookies({
    cookieString: document.cookie,
    whitelistedCookies: null,
  });

  /**
   * The most up-to-date abTests cookie are available in the document.cookie.
   * This is because the active-ab-tests headers used for server rendering may be outdated since we serve the page from cache.
   * But the document.cookie is always up-to-date, it is not cached.
   * Cloudflare worker always keeps the active abTests cookie up-to-date by setting the `setCookie` header on the response even for returning users.
   * Thus its safe to use the document.cookie here and ignore the grpcConfig.abTests set on the server.
   * This will not result in a hydration mismatch, since we are not using the abTests values in the cache-key generation for grpc requests.
   */
  if (browserCookies[AB_TEST_COOKIES.Experiments]) {
    grpcConfig.abTests = getAbTestGroups(
      browserCookies[AB_TEST_COOKIES.Experiments],
    );
  }

  const clientAuthToken = getBrowserAuthToken(defaultCookieDomain);

  const enableBotMode = getServerEnableBotModeConfigOnClient();

  const appContent = (
    <BaseProvider
      enableBotMode={enableBotMode}
      enableRealUserMode={enableRealUserMode}
      documentReferrer={document.referrer}
      storage={{
        serverCookies,
        browserCookies,
        defaultCookieDomain,
      }}
      headContext={headContext}
      loadIconSVG={loadIconSVG}
      knownIcons={iconsCache}
      hostConfig={hostConfig}
      i18nConfig={i18nConfig}
      grpcConfig={grpcConfig}
      featureFlagSetup={staticConfig.featureFlagConfig}
      routerConfig={{
        routesConfig,
        routerOptions: {
          initialLocation: {
            host: location.host,
            pathname: location.pathname,
            protocol: location.protocol?.replace(':', '') ?? 'http',
            search: location.search,
          },
        },
      }}
      authToken={clientAuthToken}
    >
      <LegacyRootProvider>
        <App />
      </LegacyRootProvider>
    </BaseProvider>
  );

  const {content, hasInitialState} = wrapAppWithDataFetcherProvider(
    appContent,
    hostConfig.environment === 'production',
  );
  const hasServerMarkupError = Boolean(
    document.querySelector('[data-tadarida-server-markup-error]'),
  );

  if (process.env.NODE_ENV === 'development') {
    import('./serverRenderingFailedAlert/ServerRenderingFailedAlert').then(
      mod => mod.renderServerRenderingFailedView(hasServerMarkupError),
    );
    // this setTimeout ensures that we also collect errors that occur after rendering
    typeof window !== 'undefined' &&
      import('./criticalErrorAlert/CriticalErrorAlert').then(mod => {
        window.setTimeout(() => {
          mod.renderCriticalErrorView();
        }, 1500);
      });
  }

  const params = new URLSearchParams(location.search);
  const ssrDisabled = params.get('ssr') === '0';

  const canHydrate = hasInitialState && !hasServerMarkupError && !ssrDisabled;

  if (!canHydrate && !ssrDisabled) {
    const logMessage =
      'Falling back to ReactDOM.render instead of ReactDOM.hydrate as no initial state is provided. This is considered an error and might have negative impact on performance. It is most probably an indicator that the server rendering did not work as expected.';

    try {
      Log.error('@aboutyou/bootstrap', logMessage);
    } catch (e) {
      console.error(logMessage);
    }
  }

  capturePreHydrationSnapshot({
    renderMode: canHydrate ? 'hydrate' : 'client-render',
    container,
  });

  const root = (() => {
    if (canHydrate) {
      return hydrateRoot(container, content);
    }

    const root = createRoot(container);
    root.render(content);
    return root;
  })();

  if (config.serviceWorker && process.env.NODE_ENV !== 'test') {
    registerServiceWorker({
      onUpdate() {
        // eslint-disable-next-line no-console
        console.log(`⚙️ Service worker has updated.`);
      },
    });
  } else {
    unregisterAllServiceWorker();
  }

  if (process.env.NODE_ENV !== 'test') {
    reportWebVitals();
  }

  return () => {
    root.unmount();
  };
};

export {startReactClientHydration};
